import { Box, Typography } from '@mui/material';
import UPButton from 'components/button';
import { toNumber } from 'lodash';
import { AvailabilitiesAlertStyled } from 'modules/availabilities/employee-availabilities/green-bar/styles';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  contractCardToCopySelector,
  isCopyModeEnabledSelector,
  totalContractCountSelector,
} from 'store/selectors/contracts-selector';
import theme from 'theme';
import { CONTRACTS__FETCH_MORE } from 'translations/constants';
import { GridHeader } from '..';
import { CardsDivider, GridColumn, GridContainer } from '../styles';
import { IContractsGridViewProps, IDayProps } from '../types';
import ContractCard from './ContractCard/ContractCard';
import PasteHereCard from './PasteHereCard';

const ContractsGridView = (props: IContractsGridViewProps): JSX.Element => {
  const {
    contractCards,
    changeContractCards,
    setOpenConfirmModal,
    onPaste,
    days,
    onChangeWeek,
    onClickEditCard,
    onClickFillSlot,
    onDeleteCheckedContracts,
    onClickReopenConfirmation,
    companyId,
    year,
    weekNumber,
    filters,
    getMoreContracts,
  } = props;
  const [t] = useTranslation();
  const isCopyModeEnabled = useAppSelector(isCopyModeEnabledSelector);
  const contractDateToCopy = useAppSelector(contractCardToCopySelector);
  const contractPageDetails = useAppSelector(totalContractCountSelector);
  const [displayFetchButton, setDisplayFetchButton] = useState<boolean>(false);

  const onChangeCardCheck = useCallback(
    contractId => {
      const newContractCards = contractCards.map(card =>
        card.contractId === contractId ? { ...card, checked: !card.checked } : card,
      );

      changeContractCards(newContractCards);
    },
    [changeContractCards, contractCards],
  );

  const checkAllContractsOfDay = (date: Date, checked: boolean) => {
    const weeklyContractIds: number[] = [];

    const updatedContracts = contractCards.map(card => {
      const contractDate = new Date(card.date);
      if (contractDate.getDate() === date.getDate() && contractDate.getMonth() === date.getMonth()) {
        if (card.isWeekly) {
          weeklyContractIds.push(card.contractId);
        }
        return {
          ...card,
          checked,
        };
      }
      return card;
    });

    const allUpdatedContracts = updatedContracts.map(card => {
      if (weeklyContractIds.includes(card.contractId)) {
        return {
          ...card,
          checked,
        };
      }
      return card;
    });

    changeContractCards(allUpdatedContracts);
  };

  const onCheckDay = (date: Date, isDayChecked: boolean) => {
    days.forEach((day: IDayProps) => {
      if (day.date.getDate() === date.getDate() && day.date.getMonth() === date.getMonth()) {
        checkAllContractsOfDay(date, isDayChecked);
      }
    });
  };

  const isPasteHereCardDisplayed = (columnDate: Date) => {
    const currentDateTime = new Date().getTime();
    const dateOfContractToCopyTime = new Date(contractDateToCopy.date).getTime();
    const columnDateTime = columnDate.getTime();
    return columnDateTime > currentDateTime && columnDateTime !== dateOfContractToCopyTime;
  };

  const displayPasteHereCardOnColumn = (contractDate: Date) => {
    if (isPasteHereCardDisplayed(contractDate)) {
      return <PasteHereCard date={contractDate} onClickPaste={onPaste} />;
    }
    return null;
  };

  const getContractsColumn = (contractDate: Date) => {
    const contractsToDisplayFiltered = contractCards.filter(
      card =>
        new Date(card.date).getDate() === contractDate.getDate() &&
        new Date(card.date).getMonth() === contractDate.getMonth() &&
        new Date(card.date).getFullYear() === contractDate.getFullYear(),
    );
    let firstActiveContractId = 0;
    const currentDate = new Date().getDate();
    const currentHour = new Date().getHours();

    // const contractsToDisplay = sortedContracts(contractsToDisplayFiltered);

    if (contractDate.getDate() === currentDate) {
      const firstActiveContract = contractCards.find(card => {
        const contractStartHour = toNumber(card.startHour.split(':')[0]);
        if (contractStartHour > currentHour) {
          return true;
        }
        return false;
      });

      if (firstActiveContract) {
        firstActiveContractId = firstActiveContract.contractDateId;
      }
    }

    return (
      <GridColumn>
        {isCopyModeEnabled && contractDateToCopy ? (
          <>
            {contractsToDisplayFiltered.map(card => (
              <Fragment key={card.contractDateId}>
                {firstActiveContractId === card.contractDateId && <CardsDivider />}
                <ContractCard
                  card={card}
                  onClickConfirm={setOpenConfirmModal}
                  isCopyEnabled={isCopyModeEnabled}
                  onChangeCardCheck={onChangeCardCheck}
                  onEdit={onClickEditCard}
                  onFillSlot={onClickFillSlot}
                  onDelete={onDeleteCheckedContracts}
                  onReopen={onClickReopenConfirmation}
                  companyId={+companyId}
                  year={year}
                  week={weekNumber}
                  filters={filters}
                />
              </Fragment>
            ))}
            {displayPasteHereCardOnColumn(contractDate)}
          </>
        ) : (
          contractsToDisplayFiltered.map(card => (
            <Fragment key={card.contractDateId}>
              {firstActiveContractId === card.contractDateId && <CardsDivider />}
              <ContractCard
                card={card}
                onClickConfirm={setOpenConfirmModal}
                isCopyEnabled={isCopyModeEnabled}
                onChangeCardCheck={onChangeCardCheck}
                onEdit={onClickEditCard}
                onFillSlot={onClickFillSlot}
                onDelete={onDeleteCheckedContracts}
                onReopen={onClickReopenConfirmation}
                companyId={+companyId}
                year={year}
                week={weekNumber}
                filters={filters}
              />
            </Fragment>
          ))
        )}
      </GridColumn>
    );
  };

  const generateColumns = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {days.map(d => (
          <Fragment key={Math.random()}>{getContractsColumn(d.date)}</Fragment>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    setDisplayFetchButton(
      toNumber(companyId) > 0 && contractPageDetails?.numberOfResults >= 35 && contractCards.length > 0,
    );
  }, [companyId, contractPageDetails, contractCards]);

  const getCurrentContractsNumber = () => {
    if (contractPageDetails) {
      return Math.min(
        contractPageDetails.pageIndex * contractPageDetails.pageSize,
        contractPageDetails.numberOfResults,
      );
    }
    return contractCards.length;
  };

  return (
    <GridContainer data-gtm-page="contracts-calendar-view">
      <GridHeader onCheckDay={onCheckDay} days={days} onChangeWeek={onChangeWeek} />
      {generateColumns()}
      {displayFetchButton && (
        <AvailabilitiesAlertStyled severity="success" icon={false}>
          <Typography mr={theme.spacing(2)} fontWeight="bold">
            {`${getCurrentContractsNumber()} / ${contractPageDetails.numberOfResults}`}
          </Typography>

          <UPButton text={t(CONTRACTS__FETCH_MORE)} onClick={() => getMoreContracts()} />
        </AvailabilitiesAlertStyled>
      )}
    </GridContainer>
  );
};

export default ContractsGridView;
